'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { getLocaleFromPath } from '@/shared/api/client/base';
import { useTravels } from '@/shared/api/client/travel';
import { getCategories, TourInformationCategory } from '@/shared/model/constants/tour-information';
import PcButton from '@/shared/ui/button/PcButton';
import PcCategoryFilterGroup from '@/shared/ui/category-filter/PcCategoryFilterGroup';
import { PlusIcon } from '@/shared/ui/svgs';
import PcTypography from '@/shared/ui/typogrphy/PcTypography';
import PcWideCard from '@/shared/ui/wide-card/PcWideCard';
import PcWideCardSkeleton from '@/shared/ui/wide-card/PcWideCardSkeleton';

const PcTourInformation = () => {
  const t = useTranslations('tour_information_menu');
  const router = useRouter();
  const swiperRef = useRef<SwiperType>(null);
  const locale = getLocaleFromPath();
  const categories = getCategories(locale);
  // 상태
  const [selectedId, setSelectedId] = useState(
    locale === 'ko-KR' ? categories[0].id : categories[0].foreignId,
  );
  const [slidesPerView, setSlidesPerView] = useState<number>(1.8);
  const [currentSlide, setCurrentSlide] = useState(0);

  // 랜덤으로 O 또는 R을 선택
  // O: 제목순, R: 생성일순 Q: 수정일순
  const randomArrange = useMemo(() => (Math.random() > 0.5 ? 'O' : 'R'), []);

  // API
  const { data: tourAllInformation, isFetching } = useTravels({
    arrange: randomArrange,
    areaCode: '1',
    numOfRows: 30,
    contentTypeId: selectedId.toString(),
    pageNo: 1,
  });

  // 조회한 데이터 중 랜덤으로 5개만 추출
  const tourInformation = useMemo(() => {
    const items = tourAllInformation?.response?.body?.items?.item || [];
    return items.sort(() => 0.5 - Math.random()).slice(0, 5);
  }, [tourAllInformation]);

  // 핸들러
  const handleSelect = (menu: TourInformationCategory) => {
    setCurrentSlide(0);
    setSelectedId(locale === 'ko-KR' ? menu.id : menu.foreignId);
    swiperRef?.current?.slideToLoop(0, 0);
  };

  const handleCardClick = (contentId: string) => {
    const pathname = categories.find((category) =>
      locale === 'ko-KR' ? category.id === selectedId : category.foreignId === selectedId,
    )?.translationKey;
    router.push(`/travel/${pathname}/${contentId}`);
  };

  const handleViewmoreClick = (selectedId: number) => {
    const pathname = categories.find((category) =>
      locale === 'ko-KR' ? category.id === selectedId : category.foreignId === selectedId,
    )?.translationKey;
    router.push(`/travel/${pathname}`);
  };

  // 반응형 슬라이드 개수 계산
  useEffect(() => {
    const calculateSlidesPerView = () => {
      const cardWidth = 845; // 카드 전체 폭 (컨텐츠 + 패딩)
      const viewportWidth = window.innerWidth;
      const containerLeftPadding = viewportWidth >= 1320 ? (viewportWidth - 1320) / 2 : 0;
      const availableWidth = viewportWidth - containerLeftPadding;

      // 사용 가능한 공간에 표시 가능한 카드 수 계산
      const rawSlides = availableWidth / cardWidth;
      setSlidesPerView(Math.max(rawSlides) - 0.14);
    };

    calculateSlidesPerView();
    window.addEventListener('resize', calculateSlidesPerView);
    return () => window.removeEventListener('resize', calculateSlidesPerView);
  }, []);

  // 슬라이더에 표시될 아이템들
  const tourItems = tourInformation || [];
  const totalItems = tourItems.length;

  // 스켈레톤 아이템 생성
  const renderSkeletons = () => {
    return Array.from({ length: 10 }).map((_, index) => (
      <SwiperSlide key={`skeleton-${index}`}>
        <PcWideCardSkeleton width={845} height={400} />
      </SwiperSlide>
    ));
  };

  // 실제 투어 아이템 렌더링
  const renderTourItems = () => {
    return tourItems.map((tour) => (
      <SwiperSlide key={tour.contentid}>
        <PcWideCard
          title={tour.title}
          content={tour.addr1}
          imagePath={tour.firstimage}
          onClick={() => handleCardClick(tour.contentid)}
        />
      </SwiperSlide>
    ));
  };

  const progressWidth = totalItems ? ((currentSlide + 1) / totalItems) * 100 : 0;

  return (
    <>
      {!tourAllInformation?.status && (
        <section className="w-full px-[30px] h-[898px] bg-[#F8F8FA]">
          <div className="max-w-[1280px] mx-auto pt-[120px] flex items-start justify-between">
            <div>
              <PcTypography variant="category">Korea Hot Place</PcTypography>
              <PcTypography variant="heading">Tour Information</PcTypography>
            </div>
            <div className="mt-[52px] flex flex-wrap">
              <PcCategoryFilterGroup
                items={categories}
                selectedId={selectedId}
                getItemId={(menu) => (locale === 'ko-KR' ? menu.id : menu.foreignId)}
                getItemLabel={(menu) => t(menu.translationKey)}
                onSelect={handleSelect}
              />
            </div>
          </div>

          {/* 슬라이더 영역 */}
          <div className="w-full mt-[34px] xl:pl-[calc((100vw-1320px)/2)]">
            <Swiper
              modules={[Navigation, Pagination]}
              loop={!isFetching && totalItems > 1}
              slidesPerView={slidesPerView}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              className="!pt-[20px] !pb-[40px] !pl-[20px] !pr-[20px]"
              onSlideChange={(swiper) => setCurrentSlide(swiper.realIndex)}
            >
              {isFetching ? renderSkeletons() : renderTourItems()}
            </Swiper>
          </div>

          {/* 하단 진행바 및 버튼 영역 */}
          <div className="max-w-[1280px] mx-auto">
            <div className="relative flex items-center">
              <div className="w-full h-[3px] bg-[#EAEDEF]">
                <div
                  className="h-full bg-secondary-black transition-all duration-300"
                  style={{ width: `${progressWidth}%` }}
                ></div>
              </div>
              <div className="flex gap-[12px] pl-[40px]">
                <PcButton
                  shape="rectangle"
                  variant="outlineDefault"
                  width={167}
                  height={60}
                  className="rounded-[40px] text-locale-18 font-medium ml-[12px]"
                  onClick={() => {
                    handleViewmoreClick(selectedId);
                  }}
                >
                  View more
                  <PlusIcon color="black" size={24} className="ml-[8px]" />
                </PcButton>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default PcTourInformation;
